import { useCallback, useRef, type PointerEvent as ReactPointerEvent } from 'react'

/** Drag distance that dismisses on release regardless of speed. */
const DISMISS_DRAG_PX = 48
/** Downward release speed that dismisses regardless of distance — a flick. */
const DISMISS_FLICK_PX_PER_MS = 0.3
/** How far back a flick's speed is measured; older samples are stale. */
const FLICK_WINDOW_MS = 100
const MIN_SAMPLE_INTERVAL_MS = 16

interface Sample {
  y: number
  time: number
}

interface DragTracker {
  pointerId: number
  samples: Array<Sample>
}

interface SheetDismissGestureHandlers {
  onPointerDownCapture: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerMoveCapture: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerUpCapture: (event: ReactPointerEvent<HTMLElement>) => void
  onPointerCancelCapture: () => void
}

/**
 * Decides a bottom sheet's swipe release with explicit distance and flick
 * thresholds. Base UI only dismisses past half the popup's height (or a very
 * fast swipe) and doesn't expose that threshold, so this reads the pointer
 * alongside its gesture and calls `onDismiss` first when the release clears
 * ours. Base UI's own release then either agrees or snaps back under the
 * closing transition. Spread the handlers onto `Drawer.Popup`.
 */
export function useSheetDismissGesture(onDismiss: () => void): SheetDismissGestureHandlers {
  const tracker = useRef<DragTracker | null>(null)

  const onPointerDownCapture = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return
    }
    tracker.current = { pointerId: event.pointerId, samples: [{ y: event.clientY, time: event.timeStamp }] }
  }, [])

  const onPointerMoveCapture = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const drag = tracker.current
    if (!drag || drag.pointerId !== event.pointerId) {
      return
    }
    const last = drag.samples[drag.samples.length - 1]
    if (event.timeStamp - last.time < MIN_SAMPLE_INTERVAL_MS) {
      return
    }
    drag.samples.push({ y: event.clientY, time: event.timeStamp })
    // Keep the start sample for total distance plus enough recent ones for the flick window.
    while (drag.samples.length > 2 && event.timeStamp - drag.samples[1].time > FLICK_WINDOW_MS) {
      drag.samples.splice(1, 1)
    }
  }, [])

  const onPointerCancelCapture = useCallback(() => {
    tracker.current = null
  }, [])

  const onPointerUpCapture = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      const drag = tracker.current
      tracker.current = null
      if (!drag || drag.pointerId !== event.pointerId) {
        return
      }
      // Base UI publishes the live drag here (DrawerPopup sets it from its swipe
      // state, see packages/react/src/drawer/popup/DrawerPopup.tsx in mui/base-ui);
      // anything else that was pressed (the handle button, a text selection)
      // leaves it at zero. Undocumented, so pin it down if Drawer is upgraded.
      const movement = Number.parseFloat(event.currentTarget.style.getPropertyValue('--drawer-swipe-movement-y'))
      if (!(movement > 0)) {
        return
      }

      const start = drag.samples[0]
      const distance = event.clientY - start.y
      const windowStart = drag.samples.find((sample) => event.timeStamp - sample.time <= FLICK_WINDOW_MS) ?? start
      const elapsed = Math.max(event.timeStamp - windowStart.time, MIN_SAMPLE_INTERVAL_MS)
      const velocity = (event.clientY - windowStart.y) / elapsed

      if (distance >= DISMISS_DRAG_PX || velocity >= DISMISS_FLICK_PX_PER_MS) {
        onDismiss()
      }
    },
    [onDismiss],
  )

  return { onPointerDownCapture, onPointerMoveCapture, onPointerUpCapture, onPointerCancelCapture }
}
